import { FC, useState } from 'react';
import * as Yup from 'yup';
import { useAppDispatch, useAppSelector } from '../../hooks/redux-hooks';
import { setToastInfo } from '../../store/common.slice';
import { ToastType } from '../../types/common.types';
import { X } from 'lucide-react';
import { ErrorMessage, FieldArray, Form, Formik } from 'formik';
import { inputFieldStyles } from '../../global-styles';
import Button from './Button';
import { ButtonVariant } from '../../types/button.enum';

const ScrapeUrlSchema = Yup.object().shape({
  urls: Yup.array()
    .of(Yup.string().url('Invalid URL').required('URL is required'))
    .min(1, 'At least one URL is required'),
});

interface ScrapeUrlDialogProps {
  onClose: () => void;
  entityId: string;
  onScrapeUrl: (urls: string[], entityId: string, orgId: string) => Promise<any>;
}

const ScrapeUrlDialog: FC<ScrapeUrlDialogProps> = ({
  onClose,
  entityId,
  onScrapeUrl,
}) => {
  const [isScraping, setIsScraping] = useState(false);
  const dispatch = useAppDispatch();
  const { orgInfo } = useAppSelector((state) => state.common);

  const handleScrape = async (values: { urls: string[] }) => {
    setIsScraping(true);
    try {
      await onScrapeUrl(values.urls, entityId, orgInfo?._id as string);
      dispatch(
        setToastInfo({
          message: 'URL scraped successfully',
          type: ToastType.SUCCESS,
          isOpen: true,
        })
      );
      onClose();
    } catch (error: any) {
      dispatch(
        setToastInfo({
          message: error?.response?.data?.message || 'Failed to scrape URL',
          type: ToastType.ERROR,
          isOpen: true,
        })
      );
    } finally {
      setIsScraping(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6 relative">
        <button
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          onClick={onClose}
          type="button"
        >
          <X />
        </button>
        <h2 className="text-xl font-semibold">Website Crawling</h2>
        <p className="text-[14px] text-appTextLight mb-4">
          Provide a website url to crawl and extract content.
        </p>
        <Formik
          initialValues={{ urls: [''] }}
          validationSchema={ScrapeUrlSchema}
          onSubmit={handleScrape}
        >
          {({ values, handleChange, handleBlur }) => (
            <Form>
              <FieldArray name="urls">
                {({ push, remove }) => (
                  <div>
                    {values.urls.map((url, index) => (
                      <div key={index} className="mb-4">
                        <label
                          htmlFor={`urls.${index}`}
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Website URL
                        </label>
                        <input
                          type="text"
                          id={`urls.${index}`}
                          name={`urls.${index}`}
                          placeholder="Enter URL to scrape"
                          className={`${inputFieldStyles} w-full`}
                          value={url}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <ErrorMessage
                          name={`urls.${index}`}
                          component="div"
                          className="text-red-500 text-sm mt-1"
                        />
                        <p className="text-appTextLight text-[12px] mt-1">
                          *Due to site-level restriction, certain website may
                          not be retrievable
                        </p>
                        {values.urls.length > 1 && (
                          <button
                            type="button"
                            className="text-red-500 text-sm mt-1"
                            onClick={() => remove(index)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </FieldArray>

              <p className="text-appTextLight text-[12px] mt-2">
                By submitting a website URL, you confirm that you have the right
                to access and extract the information available on it. Please do
                not crawl or upload any website or document containing sensitive
                or confidential data (such as health, financial, or personal
                identification information) unless you have the proper
                authorization. By using this feature, you agree that Answerr.ai
                and its affiliates are not responsible for any misuse of crawled
                data, website restrictions, or policy violations. Ensure your
                use complies with all applicable laws, website terms of service,
                and data protection regulations.
              </p>
              <div className="flex justify-end space-x-2 mt-4">
                <Button
                  type="button"
                  variant={ButtonVariant.SECONDARY}
                  onClick={onClose}
                  disabled={isScraping}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isScraping}>
                  {isScraping ? 'Scraping...' : 'Scrape'}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default ScrapeUrlDialog;
